import { useState } from "react";
import { Link } from "react-router-dom";
import { Product, CATEGORY_LABEL } from "@/mocks/products";
import { buildWhatsAppUrl, isWhatsAppConfigured } from "@/lib/whatsapp";
import { getWeChatId, isWeChatConfigured, copyWeChatId } from "@/lib/wechat";
import { useLocalized } from "@/lib/localize";
import { useBilingual } from "@/lib/bilingual";

interface ProductInfoProps {
  product: Product;
}

const ProductInfo = ({ product }: ProductInfoProps) => {
  const loc = useLocalized();
  const b = useBilingual();
  const [wechatCopied, setWechatCopied] = useState(false);

  const productName = loc(product.name); // model id, e.g. "DT101"
  const material = loc(product.material); // 材質做工
  const dimensions = loc(product.specs.dimensions);

  const whatsappReady = isWhatsAppConfigured();
  const whatsappHref = whatsappReady ? buildWhatsAppUrl({ sku: product.id }) : "/contact";

  const wechatReady = isWeChatConfigured();
  const handleWeChat = async () => {
    if (!wechatReady) return;
    const ok = await copyWeChatId();
    if (ok) {
      setWechatCopied(true);
      window.setTimeout(() => setWechatCopied(false), 1800);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Header — category tag, model number as title, material as subtitle */}
      <div>
        <div className="mb-3">
          <span className="text-amber-700 text-xs font-semibold bg-amber-50 px-3 py-1 rounded-full">
            {CATEGORY_LABEL[product.category] ? loc(CATEGORY_LABEL[product.category]) : product.category}
          </span>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-2 leading-tight">
          {productName}
        </h1>
        {material && <p className="text-stone-500 text-sm">{material}</p>}
      </div>

      {/* Spec — dimensions only */}
      <div className="bg-stone-50 rounded-xl p-4 inline-flex items-center gap-3 self-start">
        <i className="ri-ruler-line text-amber-700 text-lg" />
        <div>
          <p className="text-stone-400 text-xs mb-0.5">{b("Dimensions", "尺寸")}</p>
          <p className="text-stone-800 text-sm font-semibold">{dimensions}</p>
        </div>
      </div>

      {/* Primary action — unified inquiry (no per-product pre-order) */}
      <Link
        to="/contact"
        className="w-full bg-amber-700 hover:bg-amber-600 text-white font-bold py-4 rounded-xl transition-colors duration-200 text-sm cursor-pointer text-center"
      >
        {b("Request a Quote", "詢價報價")}
      </Link>

      {/* Chat channels — WhatsApp + WeChat */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <a
          href={whatsappHref}
          target={whatsappReady ? "_blank" : undefined}
          rel={whatsappReady ? "nofollow noreferrer" : undefined}
          title={
            whatsappReady
              ? undefined
              : b("WhatsApp number pending — opens Contact page for now.", "WhatsApp 號碼待確認——目前改開聯絡頁。")
          }
          className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-5 py-3 text-green-700 hover:bg-green-100 transition-colors cursor-pointer"
        >
          <i className="ri-whatsapp-line text-xl" />
          <div>
            <p className="text-sm font-semibold">{b("Chat on WhatsApp", "WhatsApp 洽談")}</p>
            <p className="text-xs text-green-600">
              {whatsappReady
                ? `${b("Ask about", "詢問")} ${product.id}`
                : b("[Number to be confirmed]", "[號碼待確認]")}
            </p>
          </div>
        </a>

        <button
          type="button"
          onClick={handleWeChat}
          title={
            wechatReady
              ? b("Click to copy the WeChat ID", "點擊複製微信 ID")
              : b("WeChat ID pending confirmation.", "微信 ID 待確認。")
          }
          className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-5 py-3 text-green-700 hover:bg-green-100 transition-colors cursor-pointer text-left"
        >
          <i className={`${wechatCopied ? "ri-check-line" : "ri-wechat-line"} text-xl`} />
          <div>
            <p className="text-sm font-semibold">{b("Chat on WeChat", "微信洽談")}</p>
            <p className="text-xs text-green-600">
              {wechatReady
                ? wechatCopied
                  ? b("ID copied", "已複製 ID")
                  : `${b("ID", "ID")}: ${getWeChatId()}`
                : b("[WeChat ID to be confirmed]", "[微信 ID 待確認]")}
            </p>
          </div>
        </button>
      </div>

      {/* Mobile sticky action bar */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-stone-200 px-4 py-3 flex items-center gap-2 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
        <a
          href={whatsappHref}
          target={whatsappReady ? "_blank" : undefined}
          rel={whatsappReady ? "nofollow noreferrer" : undefined}
          className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full bg-green-50 border border-green-200 text-green-700 cursor-pointer"
          aria-label={b("Chat on WhatsApp", "WhatsApp 洽談")}
        >
          <i className="ri-whatsapp-line text-lg" />
        </a>
        <button
          type="button"
          onClick={handleWeChat}
          className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full bg-green-50 border border-green-200 text-green-700 cursor-pointer"
          aria-label={b("Chat on WeChat", "微信洽談")}
        >
          <i className={`${wechatCopied ? "ri-check-line" : "ri-wechat-line"} text-lg`} />
        </button>
        <Link
          to="/contact"
          className="flex-1 bg-amber-700 hover:bg-amber-600 text-white font-bold py-3 rounded-full transition-colors duration-200 text-sm cursor-pointer text-center"
        >
          {b("Request a Quote", "詢價報價")}
        </Link>
      </div>
    </div>
  );
};

export default ProductInfo;
